import { useState, useEffect, useCallback } from "react";
import { Trash2, Pencil } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const DashboardAccountSettings = () => {
  const [, setUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [addresses, setAddresses] = useState([]);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [newAddress, setNewAddress] = useState({ label: "", city: "", fullAddress: "" });
  const [editingId, setEditingId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [deliveryCities, setDeliveryCities] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // Section-specific notifications
  const [infoMsg, setInfoMsg] = useState({ type: "", text: "" });
  const [passMsg, setPassMsg] = useState({ type: "", text: "" });
  const [addrMsg, setAddrMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/users/profile`, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) throw new Error("Failed to fetch user profile");

        const data = await res.json();
        setUser(data);
        setForm({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
        });
        setAddresses(data.addresses || []);
      } catch (err) {
        console.error("Error fetching user profile:", err);
        logout();
      }
    };

    const fetchCities = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/settings`);
        const data = await res.json();
        setDeliveryCities(data.cities || []); // ✅ use cities here
      } catch (err) {
        console.error("Error loading cities:", err);
      }
    };

    fetchUser();
    fetchCities();
  }, [BASE_URL, logout, setUser]);


  useEffect(() => {
    const timer = setTimeout(() => {
      setInfoMsg({ type: "", text: "" });
      setPassMsg({ type: "", text: "" });
      setAddrMsg({ type: "", text: "" });
    }, 4000);
    return () => clearTimeout(timer);
  }, [infoMsg, passMsg, addrMsg]);

  const handleInfoChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(`${BASE_URL}/api/users/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Failed to update");

      const updated = await res.json();
      setUser(updated);
      setInfoMsg({ type: "success", text: "Details updated successfully." });
    } catch (err) {
      console.error("Update error:", err);
      setInfoMsg({ type: "error", text: "Failed to update profile." });
    }
  };

  const isStrongPassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(password);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const { current, new: newPass, confirm } = passwordForm;

    if (newPass !== confirm) {
      setPassMsg({ type: "error", text: "New passwords do not match." });
      return;
    }

    if (!isStrongPassword(newPass)) {
      setPassMsg({
        type: "error",
        text: "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
      });
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/users/password`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // sends HttpOnly cookie
        body: JSON.stringify({
          currentPassword: current,
          newPassword: newPass,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Password change failed");
      }

      setPassMsg({ type: "success", text: data.message });

      // Logout after short delay
      setTimeout(() => {
        logout(); 
        navigate("/signin");
      }, 3000);
    } catch (err) {
      console.error("Password change error:", err);
      setPassMsg({ type: "error", text: err.message });
    } finally {
      setPasswordForm({ current: "", new: "", confirm: "" });
    }
  };

  const fetchAddresses = useCallback(async () => {
    try {
      setLoadingAddresses(true); // Start loading
      const res = await fetch(`${BASE_URL}/api/addresses`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch");

      const data = await res.json();
      setAddresses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setAddrMsg({ type: "error", text: "Could not load addresses." });
    } finally {
      setLoadingAddresses(false); // End loading
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  useEffect(() => {
    if (user) {
      fetchAddresses(); // only fetch after user is confirmed
    }
  }, [user , fetchAddresses]);


  const handleAddressChange = (e) => {
    setNewAddress({ ...newAddress, [e.target.name]: e.target.value });
  };

  const handleSaveAddress = async () => {
    const { label, city, fullAddress } = newAddress;

    if (!label || !city || !fullAddress) {
      setAddrMsg({ type: "error", text: "Please fill all fields." });
      return;
    }

    if (!fullAddress.toLowerCase().includes(city.toLowerCase())) {
      setAddrMsg({
        type: "error",
        text: `The full address must contain the selected city "${city}".`,
      });
      return;
    }

    try {
      const method = editingId ? "PATCH" : "POST";
      const endpoint = editingId
        ? `${BASE_URL}/api/addresses/${editingId}`
        : `${BASE_URL}/api/addresses`;

      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(newAddress),
      });

      if (!res.ok) throw new Error("Failed to save address");

      await fetchAddresses(); // reload list after update
      setAddrMsg({
        type: "success",
        text: editingId ? "Address updated." : "New address added.",
      });

      setNewAddress({ label: "", city: "", fullAddress: "" });
      setEditingId(null);
    } catch (err) {
      console.error("Address save failed:", err);
      setAddrMsg({ type: "error", text: "Failed to save address." });
    }
  };

  const handleEditAddress = (addr) => {
    setEditingId(addr._id);
    setNewAddress({
      label: addr.label,
      city: addr.city,
      fullAddress: addr.fullAddress,
    });
  };

  const handleDeleteAddress = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/api/addresses/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete address");

      await fetchAddresses(); // reload after delete
      setAddrMsg({ type: "success", text: "Address deleted successfully." });
    } catch (err) {
      console.error("Delete error:", err);
      setAddrMsg({ type: "error", text: "Failed to delete address." });
    }
  };

  const Notification = ({ type, text }) => {
    if (!text) return null;
    const baseClass = "text-sm px-4 py-2 rounded w-full mb-4";
    const classes =
      type === "success"
        ? "bg-green-100 text-green-700"
        : type === "error"
        ? "bg-red-100 text-red-600"
        : "bg-gray-100 text-gray-700";
    return <div className={`${baseClass} ${classes}`}>{text}</div>;
  };

  return (
    <div className="space-y-10">
      <h2 className="text-2xl font-bold">Account Settings</h2>

      {/* Personal Info Form */}
      <form onSubmit={handleInfoSubmit} className="bg-white p-6 rounded shadow space-y-4">
        <h3 className="text-xl font-semibold mb-2">Personal Information</h3>
        <Notification type={infoMsg.type} text={infoMsg.text} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Full Name</label>
            <input type="text" name="name" value={form.name} onChange={handleInfoChange} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input type="email" name="email" value={form.email} onChange={handleInfoChange} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone Number</label>
            <input type="tel" name="phone" value={form.phone} onChange={handleInfoChange} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <button type="submit" className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Update Info</button>
      </form>

      {/* Password Change Form */}
      <form onSubmit={handlePasswordSubmit} className="bg-white p-6 rounded shadow space-y-4">
        <h3 className="text-xl font-semibold mb-2">Change Password</h3>
        <Notification type={passMsg.type} text={passMsg.text} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Current Password</label>
            <input type="password" name="current" value={passwordForm.current} onChange={handlePasswordChange} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input type="password" name="new" value={passwordForm.new} onChange={handlePasswordChange} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirm New Password</label>
            <input type="password" name="confirm" value={passwordForm.confirm} onChange={handlePasswordChange} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <button type="submit" className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Change Password</button>
      </form>

      {/* Address Manager */}
      <div className="bg-white p-6 rounded shadow space-y-6">
        <h3 className="text-xl font-semibold mb-2">Saved Addresses</h3>
        <Notification type={addrMsg.type} text={addrMsg.text} />
        {loadingAddresses ? (
            <p className="text-sm text-gray-500">Loading addresses...</p>
          ) : addresses.length === 0 ? (
              <p className="text-gray-600 text-sm">No saved addresses yet.</p>
            ) : (
              <ul className="space-y-3">
                {addresses.map((addr) => (
                  <li key={addr._id} className="border p-3 rounded flex justify-between items-stretch gap-4">
                    <div className="flex-1">
                      <p className="font-bold">{addr.label}</p>
                      <p className="text-sm text-gray-700">{addr.fullAddress}</p>
                      <p className="text-xs text-gray-500">City: {addr.city}</p>
                    </div>
                    <div className="flex flex-col justify-end gap-2">
                      <button onClick={() => handleEditAddress(addr)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        <Pencil size={14} /> Edit
                      </button>
                      <button onClick={() => handleDeleteAddress(addr._id)} className="text-sm text-red-500 hover:underline flex items-center gap-1">
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

        <div className="border-t pt-4">
  <h3 className="text-xl font-semibold mb-2 mt-4">
    {editingId ? "Edit Address" : "Add New Address"}
  </h3>

  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {/* Label Input */}
    <div className="flex flex-col">
      <label htmlFor="label" className="text-sm font-medium mb-1">
        Address Label
      </label>
      <input
        id="label"
        type="text"
        name="label"
        placeholder="e.g. Home, Office"
        value={newAddress.label}
        onChange={handleAddressChange}
        className="border px-3 py-2 rounded"
      />
    </div>

    {/* City Select */}
    <div className="flex flex-col">
      <label htmlFor="city" className="text-sm font-medium mb-1">
        City
      </label>
      <select
        id="city"
        name="city"
        value={newAddress.city}
        onChange={handleAddressChange}
        className="border px-3 py-2 rounded"
      >
        <option value="">-- Select City --</option>
        {deliveryCities.map((city) => (
          <option key={city} value={city}>{city}</option>
        ))}
      </select>
    </div>

    {/* Full Address Textarea */}
    <div className="flex flex-col col-span-1 sm:col-span-2">
      <label htmlFor="fullAddress" className="text-sm font-medium mb-1">
        Full Address
      </label>
      <textarea
        id="fullAddress"
        name="fullAddress"
        rows={2}
        placeholder="Enter the complete address here"
        value={newAddress.fullAddress}
        onChange={handleAddressChange}
        className="border px-3 py-2 rounded"
      />
    </div>
  </div>

    <button
      onClick={handleSaveAddress}
      className="mt-3 px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
    >
      {editingId ? "Update Address" : "Add Address"}
    </button>
  </div>

      </div>
    </div>
  );
};

export default DashboardAccountSettings;
